import { useMemo } from "react";

import type { ChartTableSelection } from "../../lib/chartTables.js";
import { SHARED_FULL_WEEKS_CRITERIA, SHARED_TABLES_CRITERIA } from "../../lib/chartTables.js";
import { selectedDayUptime } from "../../lib/chartTableSeries.js";
import {
  dailyChartBarAxisProps,
  dailyChartRotateAxisLabels,
  formatDashboardDayLabelLong,
} from "../../lib/dashboardDayLabel.js";
import { formatGbpPence } from "../../lib/formatMoney.js";
import {
  formatWeekAxisLabel,
  FULL_WEEKS_EMPTY_MESSAGE,
  groupFullMondayWeeks,
  shiftReportingDayKey,
  type UptimeWeekBucket,
} from "../../lib/uptimeWeekBuckets.js";
import { useSharedChartTables } from "../../lib/useSharedChartTables.js";
import type { FilterState, RetentionData } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import type { BarChartItem } from "../charts/BarChart.js";
import { BarChart } from "../charts/BarChart.js";
import { ChartTablePicker } from "./ChartTablePicker.js";

/** Same green as Gross Rev / payment confirmed. */
const REVENUE_FILL = "#31c48d";
/** Normalised £/uptime-h — same hue, semi-transparent (Uptime expected-band pattern). */
const REVENUE_PER_UPTIME_FILL = "rgba(49, 196, 141, 0.35)";

/** Above this, £ labels on the bar tops overlap and are omitted. */
const VALUE_LABEL_MAX_BARS = 7;

type RevenueDay = RetentionData["revenueByDay"][number];

function formatGbpAxisTick(pounds: number): string {
  if (pounds >= 1000) return `£${Math.round(pounds).toLocaleString("en-GB")}`;
  if (pounds >= 10) return `£${Math.round(pounds)}`;
  if (pounds > 0 && pounds < 1) return `£${pounds.toFixed(2)}`;
  return `£${(Math.round(pounds * 10) / 10).toFixed(pounds % 1 === 0 ? 0 : 1)}`;
}

function formatRateAxisTick(poundsPerHour: number): string {
  if (poundsPerHour >= 10) return `£${Math.round(poundsPerHour)}/h`;
  if (poundsPerHour > 0 && poundsPerHour < 1) return `£${poundsPerHour.toFixed(2)}/h`;
  return `£${(Math.round(poundsPerHour * 10) / 10).toFixed(poundsPerHour % 1 === 0 ? 0 : 1)}/h`;
}

function dayUptimeRate(revenuePence: number, uptimeHours: number): number | null {
  return uptimeHours > 0 ? revenuePence / 100 / uptimeHours : null;
}

function weekTotals(
  week: UptimeWeekBucket,
  byDay: ReadonlyMap<string, RevenueDay>,
): { revenuePence: number; rate: number | null } {
  let revenuePence = 0;
  for (let i = 0; i < 7; i++) {
    const day = shiftReportingDayKey(week.monday, i);
    revenuePence += (day && byDay.get(day)?.revenuePence) || 0;
  }
  // Same grain as the header overall rate: summed £ ÷ summed uptime. Do not drop the
  // series when a day was unmeasured — that hid the right axis for every real week.
  const rate = week.uptimeHours > 0 ? revenuePence / 100 / week.uptimeHours : null;
  return { revenuePence, rate };
}

const CRITERIA = `Solid green (left axis): sum of effective **payment_confirmed** amounts (£) per reporting day — same grain as Gross Rev. / venue payments CSV (date + venue + device filters; **no Game**).

Semi-transparent green (right axis): **£ per fleet-additive uptime hour** that day (revenue ÷ Health uptime hours across tables in scope). Rate is **—** only when uptime is 0. If the day has any time before heartbeat collection started, the tooltip says so; the rate is still plotted from measured uptime (same as Full weeks / Interaction Stints per uptime hour).

**Full weeks** (checkbox, off by default; shared URL \`weeks=1\`): one bar per complete **Monday–Sunday** whose seven days are all inside the filter window and each have a full day's capacity (today-so-far and future days do not count — same rule as Health Uptime by day). Amounts and uptime hours are **summed**. The faded twin is still **£ ÷ uptime hours** for that week (right axis). It is **—** only when the week has no uptime. If a day was not fully measured, the tooltip says so; the rate is still plotted. Partial weeks at the edges are omitted.

Grouped bars share a category; axes are independent (£ vs £/h). Value labels above bars are omitted when there are more than 7 days or weeks. Axis labels: **D/M** + weekday initial; weekends bold white. Dense ranges rotate −90°. Week axis label is the span (18–24/8). No Raw Events drill-down.

${SHARED_TABLES_CRITERIA}

${SHARED_FULL_WEEKS_CRITERIA}`;

export function RevenueByDayCard({
  rows: fleetRows,
  tableCount: fleetTableCount,
  devices,
  uptimeByDeviceDay,
  revenueByDeviceDay,
  chartTables,
  onChartTablesChange,
  fullWeeks,
  onFullWeeksChange,
  filters,
}: {
  rows: RevenueDay[];
  tableCount: number;
  devices: RetentionData["chartDevices"];
  uptimeByDeviceDay: RetentionData["uptimeByDeviceDay"];
  revenueByDeviceDay: RetentionData["revenueByDeviceDay"];
  chartTables: ChartTableSelection;
  onChartTablesChange: (next: ChartTableSelection) => void;
  fullWeeks: boolean;
  onFullWeeksChange: (next: boolean) => void;
  filters: FilterState;
}) {
  const tables = useSharedChartTables(devices, chartTables, onChartTablesChange, filters);
  const rows = useMemo(() => {
    if (tables.useFleetSeries) {
      return fleetRows.map((row) => ({
        ...row,
        revenuePerUptimeHour: dayUptimeRate(row.revenuePence, row.uptimeHours),
      }));
    }
    if (tables.effectiveIds.size === 0) return [];
    return fleetRows.map((row) => {
      const up = selectedDayUptime(uptimeByDeviceDay, tables.effectiveIds, row.day);
      let revenuePence = 0;
      for (const payment of revenueByDeviceDay) {
        if (payment.day === row.day && tables.effectiveIds.has(payment.deviceId)) {
          revenuePence += payment.revenuePence;
        }
      }
      return {
        day: row.day,
        revenuePence,
        uptimeHours: up.uptimeHours,
        revenuePerUptimeHour: dayUptimeRate(revenuePence, up.uptimeHours),
        capacityHours: up.capacityHours,
        unmeasuredHours: up.unmeasuredHours,
      };
    });
  }, [
    fleetRows,
    revenueByDeviceDay,
    tables.effectiveIds,
    tables.useFleetSeries,
    uptimeByDeviceDay,
  ]);
  const tableCount = tables.useFleetSeries ? fleetTableCount : tables.effectiveIds.size;
  const byDay = useMemo(() => new Map(rows.map((row) => [row.day, row])), [rows]);

  const weeks = useMemo(() => {
    if (!fullWeeks || tableCount <= 0) return [];
    return groupFullMondayWeeks(
      rows.map((row) => ({
        day: row.day,
        uptimeHours: row.uptimeHours,
        scheduledHours: 0,
        capacityHours: row.capacityHours,
        unmeasuredHours: row.unmeasuredHours,
        expectedHours: 0,
      })),
      tableCount,
    );
  }, [fullWeeks, rows, tableCount]);

  const bars: BarChartItem[] = useMemo(() => {
    if (fullWeeks) {
      return weeks.map((week) => {
        const { revenuePence, rate } = weekTotals(week, byDay);
        return {
          label: formatWeekAxisLabel(week.monday, week.sunday),
          value: revenuePence / 100,
          secondaryValue: rate ?? 0,
          secondaryFill: REVENUE_PER_UPTIME_FILL,
          fill: REVENUE_FILL,
          valueLabelLines: [
            formatGbpPence(revenuePence),
            rate == null ? "—/h" : formatRateAxisTick(rate),
          ],
        };
      });
    }
    return rows.map((row) => {
      const pounds = row.revenuePence / 100;
      const rate = row.revenuePerUptimeHour;
      return {
        ...dailyChartBarAxisProps(row.day),
        value: pounds,
        secondaryValue: rate ?? 0,
        secondaryFill: REVENUE_PER_UPTIME_FILL,
        fill: REVENUE_FILL,
        valueLabelLines: [
          formatGbpPence(row.revenuePence),
          rate == null ? "—/h" : formatRateAxisTick(rate),
        ],
      };
    });
  }, [byDay, fullWeeks, rows, weeks]);

  const shownRevenuePence = fullWeeks
    ? weeks.reduce((sum, week) => sum + weekTotals(week, byDay).revenuePence, 0)
    : rows.reduce((sum, row) => sum + row.revenuePence, 0);
  const shownUptimeHours = fullWeeks
    ? weeks.reduce((sum, week) => sum + week.uptimeHours, 0)
    : rows.reduce((sum, row) => sum + row.uptimeHours, 0);
  const overallRate = shownUptimeHours > 0 ? shownRevenuePence / 100 / shownUptimeHours : null;

  const showValueLabels = bars.length > 0 && bars.length <= VALUE_LABEL_MAX_BARS;

  return (
    <ChartCard
      compact
      title="Revenue"
      titleRight={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] font-medium text-text-primary">
            <input
              type="checkbox"
              className="accent-accent-2"
              checked={fullWeeks}
              onChange={(e) => onFullWeeksChange(e.target.checked)}
            />
            Full weeks
          </label>
          <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
            {formatGbpPence(shownRevenuePence)}
            {overallRate != null ? ` · ${formatRateAxisTick(overallRate)} overall` : ""}
          </span>
        </div>
      }
      criteria={CRITERIA}
      note={
        fullWeeks
          ? "One bar per complete Monday–Sunday in the range. Partial weeks (including this week if it has not finished) are omitted. Green = revenue; faded twin = £/uptime-h."
          : "Normalises daily take by how long tables were up. Green matches Gross Rev.; faded twin = £/uptime-h."
      }
    >
      <div className="mb-1 flex justify-end">
        <ChartTablePicker
          devices={devices}
          selectedIds={tables.effectiveIds}
          onSelectedIdsChange={tables.onSelectedIdsChange}
          locked={tables.filterLocksSingle}
        />
      </div>
      {!tables.useFleetSeries && tables.effectiveIds.size === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">Select at least one table.</p>
      ) : fullWeeks && bars.length === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">
          {FULL_WEEKS_EMPTY_MESSAGE}
        </p>
      ) : (
        <BarChart
          data={bars}
          fillBars
          fillWidth
          compact={dailyChartRotateAxisLabels(bars.length)}
          verbatimAxisLabels
          rotateAxisLabels={dailyChartRotateAxisLabels(bars.length)}
          axisLabelMaxChars={fullWeeks ? 12 : 10}
          showYAxis
          showSecondaryYAxis
          showValueLabels={showValueLabels}
          formatYAxisTick={formatGbpAxisTick}
          formatSecondaryYAxisTick={formatRateAxisTick}
          valueLabelFontSize={9}
          legend={[
            { label: "Revenue", fill: REVENUE_FILL },
            { label: "£ / uptime-h", fill: REVENUE_PER_UPTIME_FILL },
          ]}
          barTooltip={(item, index) => {
            if (fullWeeks) {
              const week = weeks[index];
              if (!week) return item.label;
              const { revenuePence, rate } = weekTotals(week, byDay);
              const rateBit = rate == null ? "—" : formatRateAxisTick(rate);
              const unmeasuredBit =
                week.unmeasuredHours > 0
                  ? " A day in the week was not fully measured; rate is still week revenue ÷ week uptime."
                  : "";
              return `${formatDashboardDayLabelLong(week.monday)} – ${formatDashboardDayLabelLong(week.sunday)}: ${formatGbpPence(revenuePence)} revenue · ${rateBit} (£ ÷ ${week.uptimeHours.toFixed(1)}h fleet uptime).${unmeasuredBit} No Raw Events drill-down from this chart.`;
            }
            const row = rows[index];
            if (!row) return item.label;
            const rate =
              row.revenuePerUptimeHour == null
                ? "—"
                : formatRateAxisTick(row.revenuePerUptimeHour);
            const unmeasuredBit =
              row.unmeasuredHours > 0
                ? " Day was not fully measured before heartbeat collection; £/h is still revenue ÷ measured uptime."
                : "";
            return `${formatDashboardDayLabelLong(row.day)}: ${formatGbpPence(row.revenuePence)} revenue · ${rate} (£ ÷ ${row.uptimeHours.toFixed(1)}h fleet uptime).${unmeasuredBit} No Raw Events drill-down from this chart.`;
          }}
        />
      )}
    </ChartCard>
  );
}
